import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AutoCompleteTypeEnum } from '../models/auto-complete-type.enum';
import { AutoCompleteModel } from '../models';

import {
  SearchDegreesGQL,
  SearchLocationsGQL,
  SearchMajorsGQL,
  SearchOccupationsGQL
} from '@cpd-app/generated';

@Injectable({
  providedIn: 'root'
})
export class SearchService {
  private filteredResultsSubject = new BehaviorSubject<AutoCompleteModel[]>(null);
  public filteredResultsList$ = this.filteredResultsSubject.asObservable();

  private fuzzyResultsSubject = new BehaviorSubject<AutoCompleteModel[]>([]);
  public fuzzyResultsList$ = this.fuzzyResultsSubject.asObservable();

  public lastSearchTerm = '';

  private queryMap = {
    [AutoCompleteTypeEnum.OccupationGQL.displayName]: this.searchOccupationsGQL,
    [AutoCompleteTypeEnum.MajorGQL.displayName]: this.searchMajorsGQL,
    [AutoCompleteTypeEnum.LocationGQL.displayName]: this.searchLocationsGQL,
    [AutoCompleteTypeEnum.DegreesGQL.displayName]: this.searchDegreesGQL
  };

  constructor(
    private searchOccupationsGQL: SearchOccupationsGQL,
    private searchLocationsGQL: SearchLocationsGQL,
    private searchMajorsGQL: SearchMajorsGQL,
    private searchDegreesGQL: SearchDegreesGQL
  ) {}

  public searchGraph(
    type: AutoCompleteTypeEnum,
    searchTerm: string
  ): Observable<AutoCompleteModel[]> {
    this.lastSearchTerm = searchTerm;
    this.fuzzyResultsSubject.next([]);
    const gqlService: any = this.queryMap[type.displayName];

    if (!gqlService) {
      console.error(`Invalid AutoCompleteTypeEnum: ${type}`);
      return null;
    }

    return gqlService
      .fetch({
        searchTerm
      })
      .pipe(
        map((results: { data: any }) => this.mapResults(results.data, type))
      );
  }

  private mapResults(
    data: any,
    type: AutoCompleteTypeEnum
  ): AutoCompleteModel[] {
    const queryKey = Object.keys(this.queryMap).find(
      (key) => this.queryMap[key] === this.queryMap[type.displayName]
    );
    const rawResults = data[queryKey];
    const toModel = (item: any): AutoCompleteModel => ({
      id: item.onetCode,
      name: item.title,
      data: item
    });

    const exactItems = Array.isArray(rawResults) ? rawResults : (rawResults?.search ?? []);
    const fuzzyItems = Array.isArray(rawResults) ? [] : (rawResults?.fuzzy ?? []);

    const exact = exactItems.map(toModel);
    const fuzzy = fuzzyItems.map(toModel);

    // The two subjects stay split, because the legacy autocomplete renders them
    // as two labelled groups and merging them there would print every
    // approximate match twice.
    this.filteredResultsSubject.next(exact);
    this.fuzzyResultsSubject.next(fuzzy);

    /*
     * THE RETURN CARRIES BOTH, exact first.
     *
     * `searchOccupationsWithFuzzy` answers with `{ search, fuzzy }` and this
     * threw the fuzzy half away, so a caller that reads the return value (the
     * redesigned skills survey does; the legacy autocomplete ignores it and
     * reads the subjects) got exact matches only. `searchDegrees` and
     * `SchoolSearchService.searchSchools` already hand their callers
     * `[...search, ...fuzzy]`, and Quick Match orders the same way.
     */
    return [...exact, ...fuzzy];
  }

  public clear() {
    this.filteredResultsSubject.next(null);
    this.fuzzyResultsSubject.next([]);
  }
}
